// Trạng thái quản trị NPP trong web (docs/KE_HOACH_NPP_SANG_WEB.md §4.2–4.3).
// Admin NPP đăng nhập ở /login như mọi người; sau đó web xin một phiên quản trị riêng từ admin-api
// (POST /admin-api/auth/from-web) và giữ quyền + NPP ở đây. Quyền KHÔNG nằm trong JWT — máy chủ đọc DB mỗi
// request; giao diện đọc lại qua GET /admin-api/auth/me (khi quay lại tab, khi gặp 403).

import { useCallback, useSyncExternalStore } from 'react'
import { isPlatformOnly, type AdminPermission } from '@shoptrack/common/admin-permissions'
import { getAccessToken } from './api'
import {
  AdminApiError,
  adminGet,
  adminPost,
  refreshAdminToken,
  setAdminAccessToken,
  setAdminExpiredHandler,
  setAdminForbiddenListener,
  setAdminRefreshListener,
} from './adminApi'

export type { AdminPermission }

/** Nhà phân phối của tài khoản. Sunrise: displayName "ShopTrack". */
export interface NppInfo {
  id: string
  name: string
  displayName: string
  code: string
  isDefault: boolean
  status: string
}

/** Cờ giao diện theo cài đặt của NPP (SePay, duyệt hai người, mức chi tối thiểu, tên in phiếu, chế độ BEST). */
export interface NppFeatures {
  sepayEnabled: boolean
  requireTwoPersonPayout: boolean
  minPayoutVnd: number
  platformName: string | null
  bestAccountMode: 'single' | 'by_weight'
}

export const DEFAULT_FEATURES: NppFeatures = {
  sepayEnabled: false,
  requireTwoPersonPayout: true,
  minPayoutVnd: 100_000,
  platformName: null,
  bestAccountMode: 'by_weight',
}

export interface NppAdminState {
  /** 'idle' = chưa thử; 'ready' = có phiên quản trị; 'error' = không xin được (xem `error`) */
  status: 'idle' | 'connecting' | 'ready' | 'error'
  error: string | null
  userId: string | null
  email: string | null
  name: string | null
  isNppOwner: boolean
  permissions: string[]
  npp: NppInfo | null
  features: NppFeatures
}

const EMPTY: NppAdminState = {
  status: 'idle',
  error: null,
  userId: null,
  email: null,
  name: null,
  isNppOwner: false,
  permissions: [],
  npp: null,
  features: DEFAULT_FEATURES,
}

let state: NppAdminState = EMPTY
type Listener = (s: NppAdminState) => void
const listeners = new Set<Listener>()

export const getNppAdminState = () => state
export function subscribeNppAdmin(fn: Listener) {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
function setState(patch: Partial<NppAdminState>) {
  state = { ...state, ...patch }
  for (const l of listeners) l(state)
}

interface AccessPayload {
  userId?: string
  email?: string | null
  name?: string | null
  isSuperAdmin?: boolean
  isNppOwner?: boolean
  permissions?: string[]
  npp?: NppInfo | null
  features?: Partial<NppFeatures>
}

function featuresOf(f: Partial<NppFeatures> | undefined, prev: NppFeatures): NppFeatures {
  if (!f) return prev
  return {
    sepayEnabled: f.sepayEnabled === true,
    requireTwoPersonPayout: f.requireTwoPersonPayout ?? DEFAULT_FEATURES.requireTwoPersonPayout,
    minPayoutVnd: typeof f.minPayoutVnd === 'number' ? f.minPayoutVnd : DEFAULT_FEATURES.minPayoutVnd,
    platformName: typeof f.platformName === 'string' ? f.platformName : null,
    bestAccountMode: f.bestAccountMode === 'single' ? 'single' : 'by_weight',
  }
}

function applyAccess(r: AccessPayload) {
  setState({
    userId: r.userId ?? state.userId,
    email: r.email ?? state.email,
    name: r.name ?? state.name,
    isNppOwner: r.isNppOwner === true,
    permissions: Array.isArray(r.permissions) ? r.permissions : [],
    npp: r.npp ?? state.npp,
    features: featuresOf(r.features, state.features),
  })
}

/** `sub` của access token web — để chắc phiên quản trị lấy từ cookie là của đúng người đang đăng nhập. */
function webUserId(): string | null {
  const t = getAccessToken()
  if (!t) return null
  try {
    const part = t.split('.')[1] ?? ''
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof json.sub === 'string' ? json.sub : null
  } catch {
    return null
  }
}

/** Xin phiên quản trị mới từ phiên web (đã qua 2FA). Gửi token WEB, không phải token quản trị → fetch riêng. */
async function requestFromWeb(): Promise<(AccessPayload & { accessToken: string }) | null> {
  const webToken = getAccessToken()
  if (!webToken) return null
  const base: string = import.meta.env.VITE_ADMIN_API_BASE ?? '/admin-api'
  const res = await fetch(`${base}/auth/from-web`, {
    method: 'POST',
    headers: { authorization: `Bearer ${webToken}` },
    credentials: 'include',
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const e = json?.error ?? {}
    throw new AdminApiError(res.status, e.code ?? 'unknown', e.message ?? `HTTP ${res.status}`, e.requestId, e.details)
  }
  return json as AccessPayload & { accessToken: string }
}

// Không làm mới được bằng cookie (phiên quản trị hết hạn / bị thu hồi) → xin lại từ phiên web.
setAdminExpiredHandler(async () => {
  try {
    const r = await requestFromWeb()
    if (!r) return null
    setAdminAccessToken(r.accessToken)
    applyAccess(r)
    return r.accessToken
  } catch {
    return null
  }
})
setAdminRefreshListener((json) => applyAccess(json as AccessPayload))
setAdminForbiddenListener(() => {
  void refreshNppAccess({ minIntervalMs: 5_000 })
})

let connectInFlight: Promise<boolean> | null = null

/**
 * Kết nối phiên quản trị. `fresh` = vừa đăng nhập ở web → luôn xin phiên mới (cookie cũ có thể là của
 * người khác). Không fresh (tải lại trang) → thử cookie trước; cookie là của tài khoản khác thì xin mới.
 */
export function connectNppAdmin(opts: { fresh?: boolean } = {}): Promise<boolean> {
  if (connectInFlight) return connectInFlight
  connectInFlight = (async () => {
    setState({ status: 'connecting', error: null })
    try {
      if (!opts.fresh) {
        // Làm mới bằng cookie sha_rt — không qua handler "hết hạn" để còn so người dùng
        const base: string = import.meta.env.VITE_ADMIN_API_BASE ?? '/admin-api'
        const res = await fetch(`${base}/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: '{}',
          credentials: 'include',
        }).catch(() => null)
        if (res?.ok) {
          const json = (await res.json()) as AccessPayload & { accessToken: string; isSuperAdmin?: boolean }
          const me = webUserId()
          if (json.accessToken && json.isSuperAdmin !== true && (!me || json.userId === me)) {
            setAdminAccessToken(json.accessToken)
            applyAccess(json)
            // /refresh không trả cờ giao diện — đọc /auth/me
            await refreshNppAccess({ force: true })
            setState({ status: 'ready' })
            return true
          }
        }
      }
      const r = await requestFromWeb()
      if (!r) throw new Error('Chưa đăng nhập')
      setAdminAccessToken(r.accessToken)
      applyAccess(r)
      setState({ status: 'ready', error: null })
      return true
    } catch (e) {
      setAdminAccessToken(null)
      setState({ ...EMPTY, status: 'error', error: e instanceof Error ? e.message : 'Không mở được khu Quản trị NPP' })
      return false
    } finally {
      connectInFlight = null
    }
  })()
  return connectInFlight
}

let meInFlight: Promise<void> | null = null
let lastMeAt = 0

/** Đọc lại quyền + cờ giao diện từ admin-api. */
export function refreshNppAccess(opts: { minIntervalMs?: number; force?: boolean } = {}): Promise<void> {
  if (!opts.force && state.status !== 'ready') return Promise.resolve()
  if (meInFlight) return meInFlight
  if (opts.minIntervalMs && Date.now() - lastMeAt < opts.minIntervalMs) return Promise.resolve()
  meInFlight = (async () => {
    try {
      const r = await adminGet<AccessPayload>('/auth/me')
      lastMeAt = Date.now()
      applyAccess(r)
    } catch (e) {
      if (e instanceof AdminApiError && e.status === 401) {
        setAdminAccessToken(null)
        setState({ ...EMPTY, status: 'error', error: 'Phiên quản trị đã hết hạn — đăng nhập lại' })
      }
    } finally {
      meInFlight = null
    }
  })()
  return meInFlight
}

if (typeof window !== 'undefined') {
  // Quay lại tab: đọc lại quyền (chủ NPP có thể vừa thu hồi quyền của sub-admin)
  window.addEventListener('focus', () => {
    void refreshNppAccess({ minIntervalMs: 30_000 })
  })
}

/** Đăng xuất khu quản trị — gọi kèm đăng xuất web. */
export async function disconnectNppAdmin(): Promise<void> {
  try {
    await adminPost('/auth/logout', {})
  } catch {
    /* phiên có thể đã hết — vẫn xoá phía máy */
  }
  setAdminAccessToken(null)
  setState(EMPTY)
}

/** Lấy lại token khi cần gọi API ngoài adminApi (hiếm). */
export async function ensureAdminToken(): Promise<string | null> {
  return refreshAdminToken()
}

// ─── Quyền ────────────────────────────────────────────────────────

/** Có ít nhất một trong các quyền — khớp hasPermission ở admin-api. Không truyền quyền nào = luôn true. */
export function canWith(s: Pick<NppAdminState, 'isNppOwner' | 'permissions'>, anyOf: readonly string[]): boolean {
  if (anyOf.length === 0) return true
  if (s.isNppOwner) return anyOf.some((p) => !isPlatformOnly(p))
  return anyOf.some((p) => !isPlatformOnly(p) && s.permissions.includes(p))
}

export function useNppAdmin(): NppAdminState {
  return useSyncExternalStore(subscribeNppAdmin, getNppAdminState, getNppAdminState)
}

/** `const can = useCan(); can('commission.manage')` — tự vẽ lại khi quyền đổi. */
export function useCan(): (...anyOf: AdminPermission[]) => boolean {
  const s = useNppAdmin()
  return useCallback((...anyOf: AdminPermission[]) => canWith(s, anyOf), [s])
}

export function useNppFeatures(): NppFeatures {
  return useNppAdmin().features
}

const FALLBACK_NPP: NppInfo = { id: 'sunrise', name: 'Sunrise', displayName: 'Sunrise', code: 'SUNRISE', isDefault: true, status: 'active' }

/** NPP của tài khoản quản trị — `displayName` thay chữ "ShopTrack" nghĩa công ty cấp trên. */
export function useAdminNpp(): NppInfo {
  return useNppAdmin().npp ?? FALLBACK_NPP
}
